import React from 'react';
import {
  Box, Typography, List, ListItem, ListItemText, Accordion, AccordionSummary, AccordionDetails, Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Static Overview Content Component
// Based on InfraScripts/ResearchBase/DetailCanadaPREligibility.txt (as of early 2025)

const OverviewContent = () => (
  <Box sx={{ mb: 4 }}>
    <Typography variant="h6" gutterBottom>Overview of Major Economic Immigration Programs (Based on 2025 Document)</Typography>
    <Typography paragraph sx={{ fontStyle: 'italic', mb: 3 }}>
      This information is summarized from the documents and reflects its content as of early 2025. Program details change frequently; always consult official sources.
    </Typography>

    {/* Express Entry Accordion */}
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Express Entry System (Federal)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Manages applications for Federal Skilled Worker (FSW), Canadian Experience Class (CEC), and Federal Skilled Trades (FSTP). Candidates create profiles, get ranked by CRS score (based on age, education, language, experience, etc.), and may receive an Invitation to Apply (ITA) in regular draws.
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Processing Time (Post-ITA):</Typography>
        <Typography variant="body2" paragraph>Target ~6 months (though 6-8 months possible as of early 2025).</Typography>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Federal Skilled Worker (FSW):</Typography>
        <List dense disablePadding>
          <ListItem><ListItemText primary="Target: Skilled workers with foreign experience." /></ListItem>
          <ListItem><ListItemText primary="Experience: ≥1 year continuous skilled (TEER 0-3) in last 10 years." /></ListItem>
          <ListItem><ListItemText primary="Language: Min. CLB 7 (all skills)." /></ListItem>
          <ListItem><ListItemText primary="Education: Min. High School (ECA if foreign)." /></ListItem>
          <ListItem><ListItemText primary="Points: Must score ≥67/100 on FSW grid." /></ListItem>
          <ListItem><ListItemText primary="Funds: Proof required (unless valid job offer/working in Canada)." /></ListItem>
        </List>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Canadian Experience Class (CEC):</Typography>
        <List dense disablePadding>
          <ListItem><ListItemText primary="Target: Individuals with recent Canadian skilled work experience." /></ListItem>
          <ListItem><ListItemText primary="Experience: ≥1 year skilled (TEER 0-3) in Canada in last 3 years (non-continuous ok)." /></ListItem>
          <ListItem><ListItemText primary="Language: CLB 7 (TEER 0/1) or CLB 5 (TEER 2/3)." /></ListItem>
          <ListItem><ListItemText primary="Education: No minimum requirement." /></ListItem>
          <ListItem><ListItemText primary="Funds: Not required." /></ListItem>
        </List>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" sx={{ mt: 1 }}>Federal Skilled Trades (FSTP):</Typography>
        <List dense disablePadding>
          <ListItem><ListItemText primary="Target: Skilled tradespeople." /></ListItem>
          <ListItem><ListItemText primary="Experience: ≥2 years in eligible trade in last 5 years." /></ListItem>
          <ListItem><ListItemText primary="Job Offer/Cert: Required (Valid job offer ≥1 year OR Canadian trade certificate)." /></ListItem>
          <ListItem><ListItemText primary="Language: Min. CLB 5 (S/L), CLB 4 (R/W)." /></ListItem>
          <ListItem><ListItemText primary="Education: No formal requirement." /></ListItem>
          <ListItem><ListItemText primary="Funds: Required (unless valid job offer/working in Canada)." /></ListItem>
        </List>
      </AccordionDetails>
    </Accordion>

    {/* PNP Accordion */}
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Provincial Nominee Programs (PNPs)</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography paragraph>
          Operated by provinces/territories (except Quebec/Nunavut) to nominate candidates meeting local economic needs. Largest economic stream overall.
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Nomination Types:</Typography>
        <List dense disablePadding>
          <ListItem><ListItemText primary="Enhanced (EE-linked): Nominee gets 600 CRS points, applies via Express Entry (faster PR ~6 months)." /></ListItem>
          <ListItem><ListItemText primary="Base (Non-EE): Apply directly to province, then separate PR application to IRCC (slower PR ~15-20 months)." /></ListItem>
        </List>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>Common Eligibility Themes:</Typography>
        <List dense disablePadding>
          <ListItem><ListItemText primary="Provincial Connection: Job offer, work/study history, family ties often required or favoured." /></ListItem>
          <ListItem><ListItemText primary="Occupation: Many streams target in-demand jobs (healthcare, tech, trades, etc.)." /></ListItem>
          <ListItem><ListItemText primary="Language: Varies widely (CLB 4-7+), some target French speakers." /></ListItem>
          <ListItem><ListItemText primary="Other Factors: Education, work experience, age, intent to reside." /></ListItem>
        </List>
         <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>Processing Times:</Typography>
         <List dense disablePadding>
           <ListItem><ListItemText primary="Provincial Nomination: ~3-9 months (varies greatly)." /></ListItem>
           <ListItem><ListItemText primary="Federal PR (Post-Nomination): ~6 months (Enhanced/EE) or ~15-20 months (Base/Non-EE)." /></ListItem>
         </List>
         <Typography variant="body2" sx={{ mt: 1 }}>
           Note: Each province has multiple streams with specific criteria (e.g., Ontario's OINP, BC's BCPNP, Alberta's AAIP, etc.). Researching individual provincial programs is crucial.
         </Typography>
      </AccordionDetails>
    </Accordion>
  </Box>
);

export default OverviewContent;